import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw, Clock } from 'lucide-react';
import './JourneyTimelineMap.css';
import { Modal } from 'antd';
import { useQuery } from '@apollo/client';
import { getLiveLocationQuery } from '../../util/query/liveLocation.query';

const JourneyTimeline = ({ data, close }) => {
  const { data: liveLocationData, loading } = useQuery(getLiveLocationQuery, {
    variables: {
      employeeId: data?.employeeId,
      scheduleId: data?.scheduleId,
      date: data?.date
    },
    skip: !data,
    fetchPolicy: 'network-only'
  });

  const [journeyData, setJourneyData] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playbackSpeed] = useState(700);
  const [viewMode, setViewMode] = useState("route"); // pinpoint | route
  const [transportMode, setTransportMode] = useState("car"); // car | person

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef(null);
  const polylineRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!loading && liveLocationData?.getLiveLocation.response) {
      setJourneyData(
        liveLocationData.getLiveLocation.response.map((llv) => ({
          lat: llv?.location?.latitude,
          lng: llv?.location?.longitude,
          timestamp: llv?.createdAt,
          address: ''
        }))
      );
    }
  }, [liveLocationData?.getLiveLocation.response, loading]);

  useEffect(() => {
    if (!window.L) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.css';
      document.head.appendChild(link);

      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.js';
      script.onload = () => {
        const rotatedScript = document.createElement('script');
        rotatedScript.src = "https://cdn.jsdelivr.net/npm/leaflet-rotatedmarker@0.2.0/leaflet.rotatedMarker.min.js";
        rotatedScript.onload = initializeMap;
        document.head.appendChild(rotatedScript);
      };
      document.head.appendChild(script);
    } else {
      initializeMap();
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, [journeyData, viewMode, transportMode]);

  const handleResize = () => {
    if (mapInstanceRef.current) mapInstanceRef.current.invalidateSize();
  };

  const getBearing = (lat1, lng1, lat2, lng2) => {
    const dLon = (lng2 - lng1) * Math.PI / 180;
    const y = Math.sin(dLon) * Math.cos(lat2 * Math.PI / 180);
    const x = Math.cos(lat1 * Math.PI / 180) * Math.sin(lat2 * Math.PI / 180) -
              Math.sin(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.cos(dLon);
    const brng = Math.atan2(y, x) * 180 / Math.PI;
    return (brng + 360) % 360;
  };

  const initializeMap = () => {
    if (mapRef.current && journeyData.length > 0) {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
      }

      const map = window.L.map(mapRef.current, {
        minZoom: 8,
        maxZoom: 20,
        zoomControl: true
      }).setView([journeyData[0].lat, journeyData[0].lng], 13);

      mapInstanceRef.current = map;

      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© WFC 360 contributors'
      }).addTo(map);

      const fullCoords = journeyData.map(p => [p.lat, p.lng]);

      if (viewMode === "pinpoint") {
        markersRef.current = journeyData.map((point, index) => {
          const userPhoto = data?.photo;
          const userIcon = window.L.divIcon({
            className: "",
            html: `<div style="position:relative;width:40px;height:40px;border-radius:50%;overflow:hidden;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);">
                     <img src="${userPhoto}" style="width:100%;height:100%;object-fit:cover;"/>
                   </div>`,
            iconSize: [40, 40],
            iconAnchor: [20, 20]
          });

          const marker = window.L.marker([point.lat, point.lng], {
            icon: userIcon,
            opacity: index === 0 ? 1 : 0.3
          }).addTo(map);

          marker.bindPopup(`
            <b>Stop ${index + 1}</b><br/>
            ${new Date(point.timestamp).toLocaleString()}
          `);

          return marker;
        });

        polylineRef.current = window.L.polyline(fullCoords, {
          color: '#3b82f6',
          weight: 4,
          opacity: 1,
          dashArray: '10,5'
        }).addTo(map);

      } else {
        window.L.polyline(fullCoords, {
          color: 'red',
          weight: 3,
          dashArray: '16,6',
          opacity: 1
        }).addTo(map);

        polylineRef.current = window.L.polyline([], {
          color: '#3b82f6',
          weight: 5,
          opacity: 1
        }).addTo(map);

        const carIcon = window.L.icon({
          iconUrl: "https://cdn-icons-png.flaticon.com/512/743/743922.png",
          iconSize: [40, 40],
          iconAnchor: [20, 20]
        });

        const walkingIcon = window.L.icon({
          iconUrl: "https://cdn-icons-png.flaticon.com/512/1946/1946488.png",
          iconSize: [40, 40],
          iconAnchor: [20, 40]
        });

        // markersRef.current = window.L.marker([journeyData[0].lat, journeyData[0].lng], {
        //   icon: transportMode === "car" ? carIcon : walkingIcon,
        //   rotationAngle: 0
        // }).addTo(map);
      }

      const group = new window.L.featureGroup([window.L.polyline(fullCoords)]);
      map.fitBounds(group.getBounds().pad(0.1));

      window.addEventListener('resize', handleResize);
    }
  };

  const playJourney = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setIsPlaying(true);
    let index = currentIndex;

    intervalRef.current = setInterval(() => {
      if (index >= journeyData.length - 1) {
        setIsPlaying(false);
        clearInterval(intervalRef.current);
        return;
      }
      index++;
      setCurrentIndex(index);

      if (viewMode === "pinpoint") {
        markersRef.current.forEach((marker, i) => {
          marker.setOpacity(i <= index ? 1 : 0.3);
        });
        markersRef.current[index].openPopup();
        mapInstanceRef.current.panTo(markersRef.current[index].getLatLng());

      } else {
        const traveled = journeyData.slice(0, index + 1).map(p => [p.lat, p.lng]);
        polylineRef.current.setLatLngs(traveled);
        const prev = journeyData[index - 1];
        const curr = journeyData[index];
        markersRef.current.setLatLng([curr.lat, curr.lng]);

        if (prev && window.L.Marker.prototype.setRotationAngle) {
          const bearing = getBearing(prev.lat, prev.lng, curr.lat, curr.lng);
          markersRef.current.setRotationAngle(bearing);
        }

        mapInstanceRef.current.panTo([curr.lat, curr.lng]);
      }
    }, playbackSpeed);
  };

  const pauseJourney = () => {
    setIsPlaying(false);
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  const resetJourney = () => {
    setIsPlaying(false);
    setCurrentIndex(0);
    if (intervalRef.current) clearInterval(intervalRef.current);

    if (viewMode === "pinpoint") {
      markersRef.current.forEach((m, i) => m.setOpacity(i === 0 ? 1 : 0.3));
      markersRef.current[0].openPopup();
    } else {
      polylineRef.current.setLatLngs([]);
      markersRef.current.setLatLng([journeyData[0].lat, journeyData[0].lng]);
    }
  };

  const formatTime = (timestamp) =>
    new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const calculateDuration = () => {
    if (journeyData.length === 0) return 0;
    const start = new Date(journeyData[0].timestamp);
    const end = new Date(journeyData[journeyData.length - 1].timestamp);
    return Math.round((end - start) / (1000 * 60 * 60 * 100)) / 10;
  };

  return (
    <Modal
      open={true}
      width={'100%'}
      footer={null}
      style={{ marginTop: '-3%' }}
      closable={true}
      onCancel={close}
      className="liveLocation"
    >
      <div className="journey-map-container">
        <div className="map-wrapper">
          <div ref={mapRef} className="map-element" />

          {/* Control Panel */}
          <div className="control-panel glass-panel">
            <div className="control-header">
              <h3 className="control-title">Journey Timeline</h3>
              <div className="time-badge">
                <Clock className={`time-icon ${isPlaying ? 'spinning' : ''}`} />
                <span className="time-text">
                  {journeyData[currentIndex] && formatTime(journeyData[currentIndex].timestamp)}
                </span>
              </div>
            </div>

            {/* Toggle View Mode */}
            <div className="view-toggle">
              <button
                
                className={`toggle-btn ${viewMode === "pinpoint" ? "active" : ""}`}
                onClick={() => { setViewMode("pinpoint"); setCurrentIndex(0); }}
              >
                Pinpoint
              </button>
              <button
                className={`toggle-btn ${viewMode === "route" ? "active" : ""}`}
                onClick={() => { setViewMode("route"); setCurrentIndex(0); }}
              >
                Route
              </button>
            </div>

            {/* Transport Mode toggle (only in route mode) */}
            

            {/* Progress */}
            <div className="progress-section">
              <div className="progress-info">
                <span className="progress-step">
                  Step {currentIndex + 1} of {journeyData.length}
                </span>
                <span className="progress-percentage">
                  {Math.round(((currentIndex + 1) / journeyData.length) * 100)}%
                </span>
              </div>
              <div className="progress-container">
                <div
                  className="progress-bar"
                  style={{ width: `${((currentIndex + 1) / journeyData.length) * 100}%` }}
                />
                <div
                  className="progress-indicator"
                  style={{ left: `calc(${((currentIndex + 1) / journeyData.length) * 100}% - 8px)` }}
                />
              </div>
            </div>

            {/* Play / Pause / Reset */}
            <div className="controls-section">
              <button onClick={resetJourney} className="btn-reset" title="Reset to beginning">
                <RotateCcw className="reset-icon" />
              </button>
              <button
                onClick={isPlaying ? pauseJourney : playJourney}
                className="btn-play"
                disabled={currentIndex >= journeyData.length - 1 && !isPlaying}
              >
                {isPlaying ? <Pause className="pause-icon" /> : <Play className="play-icon" />}
              </button>
            </div>
          </div>

          {/* Summary */}
          <div>
            <div className="summary-panel glass-panel">
              <h4 className="summary-header">
                <div className="summary-indicator"></div>
                Journey Summary
              </h4>
              <div className="summary-items">
                <div className="summary-item">
                  <span className="summary-label">Total Stops:</span>
                  <span className="summary-value">{journeyData.length}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Duration:</span>
                  <span className="summary-value duration-value">{calculateDuration()}h</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Status:</span>
                  <div className="status-container">
                    <div className={`status-dot ${isPlaying ? 'playing' : 'paused'}`}></div>
                    <span className={`status-text ${isPlaying ? 'playing' : 'paused'}`}>
                      {isPlaying ? 'Playing' : 'Paused'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </Modal>
  );
};

export default JourneyTimeline;
